import React, { useEffect, useState } from 'react';
import { useData } from '../DataContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Trash2, Edit, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import useScrambleText from '../../hooks/useScrambleText';
import ImageUpload from '../ImageUpload';

const AdminReleases = () => {
  const { releases, addRelease, updateRelease, deleteRelease, loading } = useData();
  const [isOpen, setIsOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, reset, setValue } = useForm();
  const h1Ref = useScrambleText("Releases Manager");
  const [coverImage, setCoverImage] = useState('');

  useEffect(() => {
    if (editingRelease) {
      reset({
        title: editingRelease.title,
        year: editingRelease.year,
        type: editingRelease.type,
        link: editingRelease.link,
      });
      setCoverImage(editingRelease.cover || '');
    } else {
      reset({
        title: '',
        year: '',
        type: '',
        link: '',
      });
      setCoverImage('');
    }
  }, [editingRelease, reset]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const releaseData = { ...data, cover: coverImage };
      if (editingRelease) {
        await updateRelease(editingRelease.id, releaseData);
      } else {
        await addRelease(releaseData);
      }
      handleClose();
    } catch (error) {
      console.error('Failed to save release:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (release) => {
    setEditingRelease(release);
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this release?')) {
      try {
        await deleteRelease(id);
      } catch (error) {
        console.error('Failed to delete release:', error);
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setEditingRelease(null);
    reset();
    setCoverImage('');
  };

  const handleImageChange = (newImageUrl) => {
    setCoverImage(newImageUrl);
    setValue('cover', newImageUrl);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 ref={h1Ref} className="text-3xl font-heading font-bold korov-gradient-text mb-2 cursor-pointer">Releases Manager</h1>
          <p className="text-muted-foreground">Manage your discography</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-black hover:bg-primary/80" data-testid="add-release-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Release
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading text-primary">
                {editingRelease ? 'Edit Release' : 'Add New Release'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Title</label>
                <Input 
                  {...register('title', { required: true })} 
                  placeholder="Release title" 
                  className="bg-background/50"
                  data-testid="release-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Year</label>
                  <Input 
                    {...register('year', { required: true })} 
                    placeholder="2024" 
                    className="bg-background/50"
                    data-testid="release-year-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Type</label>
                  <Input 
                    {...register('type', { required: true })} 
                    placeholder="Single/EP/Album" 
                    className="bg-background/50"
                    data-testid="release-type-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Cover Image</label>
                <ImageUpload value={coverImage} onChange={handleImageChange} label="Upload Cover Art" />
                <p className="text-xs text-muted-foreground mt-2">Or enter image URL:</p>
                <Input 
                  value={coverImage} 
                  onChange={(e) => handleImageChange(e.target.value)} 
                  placeholder="https://..." 
                  className="bg-background/50 mt-2"
                  data-testid="release-cover-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Link (Spotify, Bandcamp, etc.)</label>
                <Input 
                  {...register('link')} 
                  placeholder="https://..." 
                  className="bg-background/50"
                  data-testid="release-link-input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1 bg-primary text-black hover:bg-primary/80"
                  disabled={isSubmitting}
                  data-testid="release-submit-btn"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingRelease ? 'Update Release' : 'Add Release'}
                </Button>
                <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-white/10 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead className="text-primary">Cover</TableHead>
              <TableHead className="text-primary">Title</TableHead>
              <TableHead className="text-primary">Year</TableHead>
              <TableHead className="text-primary">Type</TableHead>
              <TableHead className="text-primary text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {releases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No releases yet. Add your first release!
                </TableCell>
              </TableRow>
            ) : (
              releases.map((release) => (
                <TableRow key={release.id} className="border-white/10" data-testid={`release-row-${release.id}`}>
                  <TableCell>
                    <img src={release.cover} alt={release.title} className="w-16 h-16 object-cover rounded" />
                  </TableCell>
                  <TableCell className="font-heading text-white">{release.title}</TableCell>
                  <TableCell className="text-muted-foreground">{release.year}</TableCell>
                  <TableCell className="text-muted-foreground">{release.type}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleEdit(release)} 
                        className="hover:bg-primary/10 hover:text-primary"
                        data-testid={`edit-release-${release.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(release.id)} 
                        className="hover:bg-destructive/10 hover:text-destructive"
                        data-testid={`delete-release-${release.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminReleases;
